import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { usePortalContainer } from "@/common/hooks/use-portal-container";

/**
 * Dialog Overlay Component
 * The backdrop that appears behind the dialog
 * 
 * CRITICAL: Overlay also needs to be in portal container for dark mode
 */
export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, onClick, onMouseDown, ...props }, ref) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Stop propagation to prevent clicks from reaching elements behind the dialog
    // This prevents calendar slot selection when clicking backdrop to dismiss
    e.stopPropagation();
    
    // Call original onClick if provided
    if (onClick) {
      onClick(e);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // CRITICAL: Stop mousedown propagation - react-big-calendar uses mousedown events
    // to detect slot selection, so we must prevent mousedown from reaching the calendar
    
    // Stop both synthetic event propagation (React) and native event propagation
    e.stopPropagation();
    e.preventDefault();
    
    // Also stop the native event - React synthetic events wrap native events
    const nativeEvent = e.nativeEvent;
    if (nativeEvent) {
      nativeEvent.stopPropagation();
      nativeEvent.stopImmediatePropagation();
    }
    
    // Call original onMouseDown if provided
    if (onMouseDown) {
      onMouseDown(e);
    }
  };

  // Also attach native event listener to catch events before React synthetic events
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  
  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const handleNativeMouseDown = (e: MouseEvent) => {
      // Stop native mousedown immediately - this runs before React synthetic events
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
    };

    // Use capture phase to catch events before they reach React handlers
    overlay.addEventListener('mousedown', handleNativeMouseDown, true);

    return () => {
      overlay.removeEventListener('mousedown', handleNativeMouseDown, true);
    };
  }, []);

  // Combine refs - both the forwarded ref and our internal ref
  const combinedRef = React.useCallback((node: HTMLDivElement | null) => {
    overlayRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  }, [ref]);

  return (
    <DialogPrimitive.Overlay
      ref={combinedRef}
      className={cn(
        "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      style={{ zIndex: 9000, ...props.style }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      {...props}
    />
  );
});

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
